import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import { moveNodeCommand } from '../../commands';
import { EditorContext } from '../../contexts';
import { DocumentPath } from '../../types';
import { findNode } from '../../utils';
import { AccordionButtonProps } from './AccordionButton/AccordionButton';
import { AccordionContentProps } from './AccordionContent/AccordionContent';

type ButtonPlacement = 'top' | 'bottom';

export interface AccordionProps {
  path: DocumentPath;
  buttonPlacement: ButtonPlacement;
  peekable?: boolean;
  initialExpanded?: boolean;
}

export function Accordion({
  children,
  path,
  buttonPlacement,
  peekable,
  initialExpanded,
}: PropsWithChildren<AccordionProps>) {
  const [expanded, setExpanded] = useState(false);

  function expand() {
    setExpanded(true);
  }

  function collapse() {
    setExpanded(false);
  }

  useEffect(() => {
    setExpanded(initialExpanded || false);
  }, [initialExpanded]);

  useEditorInitializer({ path, buttonPlacement });

  return (
    <div>
      { React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) { return child; }
        return React.cloneElement(child as React.ReactElement<AccordionContentProps | AccordionButtonProps>, {
          expanded,
          expand,
          collapse,
          peekable,
        });
      }) }
    </div>
  );
}

function useEditorInitializer({
  path,
  buttonPlacement,
}: {
  path: DocumentPath,
  buttonPlacement: ButtonPlacement,
}) {
  const context = useContextSelector(EditorContext, (context) => context);
  const node = context ? findNode(context.state.document, path) : undefined;

  useEffect(() => {
    if (!context || !node) { return; }
    if (
      buttonPlacement === 'top' &&
      node.children[0].type === 'accordionContent'
    ) {
      // when button placement is top,
      // move accordion content to last
      context.dispatch(
        moveNodeCommand([...path, 0], [...path, node.children.length]),
      );
    }
    if (
      buttonPlacement === 'bottom' &&
      node.children[0].type === 'accordionButton'
    ) {
      // when button placement is bottom,
      // move accordion content to first
      context.dispatch(
        moveNodeCommand([...path, node.children.length - 1], [...path, 0]),
      );
    }
    context.setState((state) => ({ ...state, cursor: path }));
  }, [buttonPlacement]);
}
